import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { Modal } from "bootstrap"
import { I18n } from 'i18n-js'
import locales from '../locales.json'

// Connects to data-controller="admin-cegadat-api"
// Used for admin subscriber forms (client registration and tenant edit)
export default class extends Controller {
  static targets = [
    "nameInput",
    "taxNumberInput",
    "resultsContainer",
    "loadingIndicator",
    "bankAccountModal"
  ]
  static values = {
    searchUrl: String,
    detailUrl: String
  }

  connect() {
    this.searchTimeout = null
    this.currentSearchValue = ""
    this.activeInput = null
    this.bankAccounts = []
    this.selectedPrimaryAccount = null
    this.selectedSecondaryAccount = null
    
    // Initialize i18n
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || 'hu'
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  // Handle name input changes
  onNameInput(event) {
    this.activeInput = event.target
    const value = event.target.value.trim()
    if (value.length < 4) {
      this.hideResults()
      this.hideLoading()
      return
    }

    this.debounceSearch(() => this.searchByName(value))
  }

  // Handle tax number input changes
  onTaxNumberInput(event) {
    this.activeInput = event.target
    // Remove all non-digit characters (hyphens, spaces, etc.) for search
    const digitsOnly = event.target.value.trim().replace(/\D/g, '')
    
    // Allow search with at least 8 digits (partial tax number) or full 11 digits
    if (digitsOnly.length < 8) {
      this.hideResults()
      this.hideLoading()
      return
    }

    // Always send digits only (without hyphens) to the API
    this.debounceSearch(() => this.searchByTaxNumber(digitsOnly))
  }

  debounceSearch(searchFunction) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.showLoading()

    this.searchTimeout = setTimeout(() => {
      searchFunction()
      this.searchTimeout = null
    }, 1000) // 1 second debounce
  }

  async searchByName(name) {
    try {
      const response = await get(this.searchUrlValue, {
        query: { name: name, business_type: 'all' },
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.displayResults(data)
      } else {
        this.hideResults()
        this.hideLoading()
      }
    } catch (error) {
      console.error("Search error:", error)
      this.hideResults()
      this.hideLoading()
    }
  }

  async searchByTaxNumber(taxNumber) {
    try {
      const response = await get(this.searchUrlValue, {
        query: { vat_number: taxNumber, business_type: 'all' },
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.displayResults(data)
      } else {
        this.hideResults()
        this.hideLoading()
      }
    } catch (error) {
      console.error("Search error:", error)
      this.hideResults()
      this.hideLoading()
    }
  }

  displayResults(data) {
    this.hideLoading()

    if (!data.status || !data.response || !data.response.results || data.response.results.length === 0) {
      this.hideResults()
      return
    }

    if (!this.activeInput) {
      return
    }

    const results = data.response.results
    
    // Find or create container for the active input
    let container = this.activeInput.parentElement.querySelector('[data-admin-cegadat-api-target="resultsContainer"]')
    if (!container) {
      container = document.createElement("div")
      container.setAttribute("data-admin-cegadat-api-target", "resultsContainer")
      container.className = "d-none"
      this.activeInput.parentElement.appendChild(container)
    }

    // Clear previous results
    container.innerHTML = ""

    // Add Bootstrap classes to container
    container.className = "dropdown-menu show shadow"
    container.style.zIndex = "1080"
    container.classList.remove("d-none")

    const header = document.createElement("div")
    header.className = "d-flex justify-content-between align-items-center px-2 py-2 border-bottom bg-light sticky-top"
    const titleSpan = document.createElement("span")
    titleSpan.className = "small text-muted"
    titleSpan.textContent = this.getTranslation("admin.subscribers.cegadat_api.search_results_title")
    const closeBtn = document.createElement("button")
    closeBtn.type = "button"
    closeBtn.className = "btn-close"
    closeBtn.setAttribute("aria-label", this.getTranslation("admin.subscribers.cegadat_api.close_search_results"))
    closeBtn.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.hideResults()
    })
    header.appendChild(titleSpan)
    header.appendChild(closeBtn)
    container.appendChild(header)

    // Create results list
    results.forEach((company) => {
      const item = document.createElement("a")
      item.className = "dropdown-item"
      item.href = "#"
      item.innerHTML = `
        <div class="fw-bold mb-1">${this.escapeHtml(company.fullName || company.shortName || '')}</div>
        ${company.vatNumber ? `<div class="text-muted small">Adószám: ${this.formatTaxNumber(company.vatNumber)}</div>` : ''}
        ${company.status ? `<div class="text-muted small">Státusz: ${company.status}</div>` : ''}
      `

      item.addEventListener("click", (e) => {
        e.preventDefault()
        this.selectCompany(company)
      })

      container.appendChild(item)
    })
  }

  async selectCompany(company) {
    if (!company.vatNumber) {
      console.error("No VAT number for company")
      return
    }

    try {
      const response = await get(this.detailUrlValue, {
        query: { vat_number: company.vatNumber },
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        if (data.status && data.response && data.response.results) {
          this.fillForm(data.response.results)
        }
      }
    } catch (error) {
      console.error("Detail fetch error:", error)
    }

    this.hideResults()
  }

  fillForm(companyData) {
    const form = this.element.closest('form')
    if (!form) return

    // Fill basic information
    if (this.hasNameInputTarget && companyData.fullName) {
      this.nameInputTarget.value = companyData.fullName
    }

    if (this.hasTaxNumberInputTarget && companyData.vatNumber) {
      this.taxNumberInputTarget.value = this.formatTaxNumber(companyData.vatNumber)
    }

    // Fill contact information
    if (companyData.emailAddress) {
      const emailField = form.querySelector('input[name="subscriber[email]"]')
      if (emailField) emailField.value = companyData.emailAddress
    }

    if (companyData.phoneNumber) {
      const phoneField = form.querySelector('input[name="subscriber[phone_number]"]')
      if (phoneField) phoneField.value = companyData.phoneNumber
    }

    if (companyData.website) {
      const websiteField = form.querySelector('input[name="subscriber[website]"]')
      if (websiteField) websiteField.value = companyData.website
    }

    // Handle banking information - check if multiple accounts
    if (companyData.bankAccounts && companyData.bankAccounts.length > 0) {
      this.bankAccounts = companyData.bankAccounts
      
      if (companyData.bankAccounts.length > 1) {
        // Show modal for selection
        this.showBankAccountModal(companyData.bankAccounts)
      } else {
        // Single account - fill both primary and secondary with the same account
        const bankAccount = companyData.bankAccounts[0]
        this.fillBankAccounts(bankAccount, bankAccount)
      }
    }

    // Fill address information
    if (companyData.fullAddress) {
      this.parseAndFillAddress(companyData, form)
    }
  }

  showBankAccountModal(bankAccounts) {
    // Reset selections
    this.selectedPrimaryAccount = null
    this.selectedSecondaryAccount = null
    
    // Find or create modal
    let modal = document.querySelector('[data-admin-cegadat-api-target="bankAccountModal"]')
    if (!modal) {
      modal = this.createBankAccountModal()
      document.body.appendChild(modal)
    }

    // Populate modal with bank accounts
    const modalBody = modal.querySelector('.modal-body')
    modalBody.innerHTML = ''
    
    const primaryLabel = this.getTranslation('admin.subscribers.primary_bank_account')
    const secondaryLabel = this.getTranslation('admin.subscribers.secondary_bank_account')
    
    bankAccounts.forEach((account, index) => {
      const accountItem = document.createElement('div')
      accountItem.className = 'card mb-3'
      accountItem.setAttribute('data-account-index', index)
      accountItem.innerHTML = `
        <div class="card-body">
          <h6 class="card-title">${this.escapeHtml(account.bankName || this.getTranslation('admin.subscribers.cegadat_api.bank_account_modal.unknown_bank'))}</h6>
          <p class="card-text mb-3">
            <strong>${this.getTranslation('admin.subscribers.cegadat_api.bank_account_modal.account_number')}:</strong> ${this.escapeHtml(account.accountNumber || 'N/A')}
          </p>
          <div class="d-flex gap-2">
            <button type="button" class="btn btn-sm btn-primary select-primary-btn" data-account-index="${index}">
              ${this.escapeHtml(primaryLabel)}
            </button>
            <button type="button" class="btn btn-sm btn-secondary select-secondary-btn" data-account-index="${index}">
              ${this.escapeHtml(secondaryLabel)}
            </button>
          </div>
        </div>
      `
      
      // Add click handlers
      const primaryBtn = accountItem.querySelector('.select-primary-btn')
      const secondaryBtn = accountItem.querySelector('.select-secondary-btn')
      
      primaryBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.selectPrimaryAccount(account, index, primaryBtn, accountItem)
      })
      
      secondaryBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.selectSecondaryAccount(account, index, secondaryBtn, accountItem)
      })
      
      modalBody.appendChild(accountItem)
    })

    // Update confirm button state
    this.updateModalConfirmButton(modal)

    // Show modal using Bootstrap
    const bsModal = new Modal(modal)
    bsModal.show()
  }

  selectPrimaryAccount(account, index, button, card) {
    this.selectedPrimaryAccount = account
    
    // Update button state
    const allPrimaryBtns = document.querySelectorAll('.select-primary-btn')
    allPrimaryBtns.forEach(btn => {
      btn.classList.remove('active')
      btn.textContent = this.getTranslation('admin.subscribers.primary_bank_account')
    })
    button.classList.add('active')
    button.textContent = `✓ ${this.getTranslation('admin.subscribers.primary_bank_account')}`
    
    this.updateModalConfirmButton()
  }

  selectSecondaryAccount(account, index, button, card) {
    this.selectedSecondaryAccount = account
    
    // Update button state
    const allSecondaryBtns = document.querySelectorAll('.select-secondary-btn')
    allSecondaryBtns.forEach(btn => {
      btn.classList.remove('active')
      btn.textContent = this.getTranslation('admin.subscribers.secondary_bank_account')
    })
    button.classList.add('active')
    button.textContent = `✓ ${this.getTranslation('admin.subscribers.secondary_bank_account')}`
    
    this.updateModalConfirmButton()
  }

  updateModalConfirmButton(modal = null) {
    if (!modal) {
      modal = document.querySelector('[data-admin-cegadat-api-target="bankAccountModal"]')
    }
    if (!modal) return
    
    const confirmBtn = modal.querySelector('.confirm-selection-btn')
    if (confirmBtn) {
      if (this.selectedPrimaryAccount && this.selectedSecondaryAccount) {
        confirmBtn.disabled = false
        confirmBtn.classList.remove('btn-secondary')
        confirmBtn.classList.add('btn-primary')
      } else {
        confirmBtn.disabled = true
        confirmBtn.classList.remove('btn-primary')
        confirmBtn.classList.add('btn-secondary')
      }
    }
  }

  createBankAccountModal() {
    const modal = document.createElement('div')
    modal.className = 'modal fade'
    modal.setAttribute('data-admin-cegadat-api-target', 'bankAccountModal')
    modal.setAttribute('tabindex', '-1')
    modal.setAttribute('aria-labelledby', 'bankAccountModalLabel')
    modal.setAttribute('aria-hidden', 'true')
    
    const title = this.getTranslation('admin.subscribers.cegadat_api.bank_account_modal.title')
    const cancelText = this.getTranslation('admin.cancel')
    const confirmText = this.getTranslation('admin.save')
    
    modal.innerHTML = `
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="bankAccountModalLabel">${this.escapeHtml(title)}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted mb-3">${this.escapeHtml(this.getTranslation('admin.subscribers.cegadat_api.bank_account_modal.select_both'))}</p>
            <!-- Bank accounts will be populated here -->
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${this.escapeHtml(cancelText)}</button>
            <button type="button" class="btn btn-secondary confirm-selection-btn" disabled>${this.escapeHtml(confirmText)}</button>
          </div>
        </div>
      </div>
    `
    
    // Add confirm button handler
    const confirmBtn = modal.querySelector('.confirm-selection-btn')
    confirmBtn.addEventListener('click', () => {
      this.confirmBankAccountSelection()
    })
    
    return modal
  }

  confirmBankAccountSelection() {
    if (this.selectedPrimaryAccount && this.selectedSecondaryAccount) {
      this.fillBankAccounts(this.selectedPrimaryAccount, this.selectedSecondaryAccount)
      this.hideBankAccountModal()
    }
  }

  hideBankAccountModal() {
    const modal = document.querySelector('[data-admin-cegadat-api-target="bankAccountModal"]')
    if (modal) {
      const bsModal = Modal.getInstance(modal)
      if (bsModal) {
        bsModal.hide()
      } else {
        // If no instance exists, create one and hide it
        const newModal = new Modal(modal)
        newModal.hide()
      }
    }
  }

  fillBankAccounts(primaryAccount, secondaryAccount) {
    const form = this.element.closest('form')
    if (!form) return

    // Fill primary bank account
    if (primaryAccount) {
      if (primaryAccount.bankName) {
        const primaryBankNameField = form.querySelector('input[name="subscriber[primary_bank_name]"]')
        if (primaryBankNameField) {
          primaryBankNameField.value = primaryAccount.bankName
        }
      }
      if (primaryAccount.accountNumber) {
        const primaryBankAccountField = form.querySelector('input[name="subscriber[primary_bank_account]"]')
        if (primaryBankAccountField) {
          primaryBankAccountField.value = primaryAccount.accountNumber
        }
      }
    }

    // Fill secondary bank account
    if (secondaryAccount) {
      if (secondaryAccount.bankName) {
        const secondaryBankNameField = form.querySelector('input[name="subscriber[secondary_bank_name]"]')
        if (secondaryBankNameField) {
          secondaryBankNameField.value = secondaryAccount.bankName
        }
      }
      if (secondaryAccount.accountNumber) {
        const secondaryBankAccountField = form.querySelector('input[name="subscriber[secondary_bank_account]"]')
        if (secondaryBankAccountField) {
          secondaryBankAccountField.value = secondaryAccount.accountNumber
        }
      }
    }
  }

  parseAndFillAddress(companyData, form) {
    // Fill postal code
    if (companyData.addressPostalCode) {
      const postalCodeField = form.querySelector('input[name="subscriber[headquarters_postal_code]"]')
      if (postalCodeField) {
        postalCodeField.value = companyData.addressPostalCode
        // Trigger postcode controller if present
        const postcodeEvent = new Event('keyup', { bubbles: true })
        postalCodeField.dispatchEvent(postcodeEvent)
      }
    }

    // Fill city
    if (companyData.addressCity) {
      const cityField = form.querySelector('input[name="subscriber[headquarters_city]"]')
      if (cityField) cityField.value = companyData.addressCity
    }

    // Fill country
    const countryField = form.querySelector('input[name="subscriber[headquarters_country]"]')
    if (countryField) countryField.value = "Magyarország"

    // Parse street address from addressStreet field
    if (companyData.addressStreet) {
      const streetPattern = /^(.+?)\s+(utca|út|tér|körút|sétány|sor|park|aluljáró|átjáró|forduló|lejáró|rakpart|sugárút|fasor|köz|liget)\s+(\d+.*)$/i
      const match = companyData.addressStreet.match(streetPattern)
      
      if (match) {
        const streetName = match[1].trim()
        const streetType = match[2].trim()
        const houseNumber = match[3].trim().replace(/\.$/, '')

        const streetTypeField = form.querySelector('input[name="subscriber[headquarters_area_type]"]')
        if (streetTypeField) streetTypeField.value = streetType

        const streetNameField = form.querySelector('input[name="subscriber[headquarters_area_name]"]')
        if (streetNameField) streetNameField.value = streetName

        const houseNumberField = form.querySelector('input[name="subscriber[headquarters_house_number]"]')
        if (houseNumberField) houseNumberField.value = houseNumber
      } else {
        const simplePattern = /^(.+?)\s+(\d+.*)$/
        const simpleMatch = companyData.addressStreet.match(simplePattern)
        
        if (simpleMatch) {
          const streetName = simpleMatch[1].trim()
          const houseNumber = simpleMatch[2].trim().replace(/\.$/, '')

          const streetNameField = form.querySelector('input[name="subscriber[headquarters_area_name]"]')
          if (streetNameField) streetNameField.value = streetName

          const houseNumberField = form.querySelector('input[name="subscriber[headquarters_house_number]"]')
          if (houseNumberField) houseNumberField.value = houseNumber
        } else {
          const streetNameField = form.querySelector('input[name="subscriber[headquarters_area_name]"]')
          if (streetNameField) streetNameField.value = companyData.addressStreet
        }
      }
    }
  }

  hideResults() {
    const containers = this.element.querySelectorAll('[data-admin-cegadat-api-target="resultsContainer"]')
    containers.forEach(container => {
      container.classList.remove("show")
      container.classList.add("d-none")
      container.innerHTML = ""
      container.className = "d-none"
    })
  }

  showLoading() {
    if (!this.activeInput) return
    
    let indicator = this.activeInput.parentElement.querySelector('[data-admin-cegadat-api-target="loadingIndicator"]')
    if (!indicator) {
      indicator = document.createElement("div")
      indicator.className = "position-absolute top-50 end-0 translate-middle-y d-none me-2"
      indicator.setAttribute("data-admin-cegadat-api-target", "loadingIndicator")
      indicator.innerHTML = `
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">Keresés...</span>
        </div>
      `
      this.activeInput.parentElement.appendChild(indicator)
    }
    indicator.classList.remove("d-none")
  }

  hideLoading() {
    const indicators = this.element.querySelectorAll('[data-admin-cegadat-api-target="loadingIndicator"]')
    indicators.forEach(indicator => {
      indicator.classList.add("d-none")
    })
  }

  formatTaxNumber(taxNumber) {
    if (!taxNumber) return ''
    
    const digits = taxNumber.replace(/\D/g, '')
    
    if (digits.length >= 11) {
      return `${digits.substring(0, 8)}-${digits.substring(8, 9)}-${digits.substring(9, 11)}`
    } else if (digits.length >= 9) {
      return `${digits.substring(0, 8)}-${digits.substring(8, 9)}-${digits.substring(9)}`
    } else if (digits.length >= 8) {
      return `${digits.substring(0, 8)}-${digits.substring(8)}`
    }
    
    return digits
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  getTranslation(key) {
    return this.i18n?.t(key) || key
  }
}

